import type {
  Industry,
  GlobalConfig,
  Template,
  ElementType,
} from '@/utils/builder/types';
import type { WebsiteInterfacePageType } from '@/interfaces/interface.interface';
import { generateId } from '@/utils/builder/id-utils';

// Template config registry - maps industry to their config getters
// This allows lazy loading of template configs
type GlobalConfigGetter = () => GlobalConfig;

const globalConfigRegistry: Partial<Record<Industry, GlobalConfigGetter>> = {};

// Register a global config for an industry
export const registerGlobalConfig = (
  industry: Industry,
  getter: GlobalConfigGetter
): void => {
  globalConfigRegistry[industry] = getter;
};

// Get global config for an industry with fallback chain
export const getGlobalConfigByIndustry = async (
  industry: Industry
): Promise<GlobalConfig> => {
  // Define fallback mappings
  const fallbackMap: Partial<Record<Industry, Industry>> = {
    templateUpsell3: 'template3',
    templateDownsell3: 'template3',
    'template4-upsell': 'template4',
    'template4-downsell': 'template4',
    'template1-downsell': 'template1',
    blankTemplate: 'template1',
  };

  const targetIndustry = fallbackMap[industry] || industry;
  const getter =
    globalConfigRegistry[targetIndustry] || globalConfigRegistry['template1'];

  if (getter) {
    return getter();
  }

  // Dynamic import fallback
  const { template1GlobalConfig } = await import('@/templates/template1');
  return template1GlobalConfig;
};

// Synchronous version for backward compatibility
export const getGlobalConfigByIndustrySync = (
  industry: Industry
): GlobalConfig | null => {
  const fallbackMap: Partial<Record<Industry, Industry>> = {
    templateUpsell3: 'template3',
    templateDownsell3: 'template3',
    'template4-upsell': 'template4',
    'template4-downsell': 'template4',
    'template1-downsell': 'template1',
    blankTemplate: 'template1',
  };

  const targetIndustry = fallbackMap[industry] || industry;
  const getter =
    globalConfigRegistry[targetIndustry] || globalConfigRegistry['template1'];

  return getter ? getter() : null;
};

// Initialize registry with all template configs
export const initializeTemplateRegistry = async (): Promise<void> => {
  const [
    { template1GlobalConfig },
    { template2GlobalConfig },
    { template3GlobalConfig },
    { template4GlobalConfig },
    { template1UpsellGlobalConfig },
    { template2UpsellGlobalConfig },
    { template2DownsellGlobalConfig },
    { template2ThankyouGlobalConfig },
    { template2ContactGlobalConfig },
    { template2PrivacyPolicyGlobalConfig },
    { template2TermsGlobalConfig },
    { template2ReturnsGlobalConfig },
    { template2DisclaimerGlobalConfig },
  ] = await Promise.all([
    import('@/templates/template1'),
    import('@/templates/template2'),
    import('@/templates/template3'),
    import('@/templates/template4'),
    import('@/templates/template1-upsell'),
    import('@/templates/template2-upsell'),
    import('@/templates/template2-downsell'),
    import('@/templates/template2-thankyou'),
    import('@/templates/template2-contact'),
    import('@/templates/template2-privacy-policy'),
    import('@/templates/template2-terms'),
    import('@/templates/template2-returns'),
    import('@/templates/template2-disclaimer'),
  ]);

  registerGlobalConfig('template1', () => template1GlobalConfig);
  registerGlobalConfig('template2', () => template2GlobalConfig);
  registerGlobalConfig('template3', () => template3GlobalConfig);
  registerGlobalConfig('template4', () => template4GlobalConfig);
  registerGlobalConfig('template1-upsell', () => template1UpsellGlobalConfig);
  registerGlobalConfig('template2-upsell', () => template2UpsellGlobalConfig);
  registerGlobalConfig(
    'template2-downsell',
    () => template2DownsellGlobalConfig
  );
  registerGlobalConfig(
    'template2-thankyou',
    () => template2ThankyouGlobalConfig
  );
  registerGlobalConfig('template2-contact', () => template2ContactGlobalConfig);
  registerGlobalConfig(
    'template2-privacy-policy',
    () => template2PrivacyPolicyGlobalConfig
  );
  registerGlobalConfig('template2-terms', () => template2TermsGlobalConfig);
  registerGlobalConfig('template2-returns', () => template2ReturnsGlobalConfig);
  registerGlobalConfig(
    'template2-disclaimer',
    () => template2DisclaimerGlobalConfig
  );
};

// Default template for new projects
export const defaultTemplate: Template = {
  id: generateId(),
  name: 'Default Template',
  industry: 'other' as Industry,
  elements: [
    {
      id: generateId(),
      type: 'container' as ElementType,
      styles: {
        backgroundColor: '#FFFFFF',
        padding: '20px',
        margin: '0 0 20px 0',
      },
      children: [
        {
          id: generateId(),
          type: 'heading' as ElementType,
          content: 'Welcome to LandingPage Builder',
          styles: {
            fontSize: '32px',
            fontWeight: 'bold',
            textAlign: 'center',
            color: '#333333',
            margin: '0 0 20px 0',
          },
        },
        {
          id: generateId(),
          type: 'paragraph' as ElementType,
          content:
            'This is a sample website. Drag elements from the sidebar to build your own page.',
          styles: {
            fontSize: '16px',
            textAlign: 'center',
            color: '#666666',
            margin: '0 0 20px 0',
          },
        },
        {
          id: generateId(),
          type: 'button' as ElementType,
          content: 'Get Started',
          styles: {
            backgroundColor: '#7C3AED',
            color: '#FFFFFF',
            padding: '10px 25px',
            borderRadius: '4px',
            fontWeight: 'bold',
            fontSize: '16px',
            margin: '0 auto',
            display: 'block',
          },
        },
      ],
    },
  ],
  rootContainer: {
    styles: {
      backgroundColor: 'background',
      color: 'forground',
      padding: '0px',
      margin: '0px',
      width: '100%',
      minHeight: '100vh',
      display: 'block',
      position: 'relative',
    },
    responsiveStyles: {},
    hoverStyles: {},
    responsiveHoverStyles: {},
  },
};

// Get available page types for an industry
export const getInterfacePageTypesByIndustry = (
  industry: Industry
): WebsiteInterfacePageType[] => {
  const pageTypes: Record<string, WebsiteInterfacePageType[]> = {
    blankTemplate: ['default'],
    template1: [
      'default',
      'vsl',
      'upsell',
      'downsell',
      'thankyou',
      'contact',
      'privacy',
      'returns',
      'terms',
      'disclaimer',
    ],
    template2: [
      'default',
      'vsl',
      'upsell',
      'downsell',
      'thankyou',
      'contact',
      'privacy',
      'returns',
      'terms',
      'disclaimer',
    ],
    template3: [
      'default',
      'upsell',
      'downsell',
      'thankyou',
      'contact',
      'privacy',
      'returns',
      'terms',
      'disclaimer',
    ],
    template4: ['default', 'upsell', 'downsell'],
  };
  return pageTypes[industry] || ['default'];
};
